import { useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, ChevronRight, CircleHelp, ListChecks, XCircle } from "lucide-react";
import { GateBlock, VerifyBlock } from "./agentic-ui";
import { Chip } from "./item-ui";
import {
  agentFor,
  destinationOf,
  fmtDate,
  fmtDateTime,
  humanizeCheckName,
  kindOf,
  originOf,
  parseQa,
  qaTone,
  qaVerdict,
  unverifiedOf,
  wordCountOf,
  type QaSummary,
  type StudioItem,
} from "./helpers";
import { itemPlatformConnected, wordpressConfigured } from "./use-studio";

/** True once the viewport is at least `px` wide (re-evaluated when it is resized). */
export function useMinWidth(px: number): boolean {
  const query = `(min-width:${px}px)`;
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

const CHECKS_SHOWN = 6;

function Row({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 py-1.5" data-testid={testId}>
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm text-foreground">{children}</dd>
    </div>
  );
}

function QaBlock({ qa, graded }: { qa: QaSummary | null; /** The item carries a score card or an independent QA review, so "not scored" would contradict what is shown beside it. */ graded: boolean }) {
  const [all, setAll] = useState(false);
  if (!qa) {
    return graded ? (
      <div data-testid="checks-qa" data-qa="graded">
        <p className="text-sm font-medium text-foreground">No overall score</p>
        <p className="mt-0.5 text-xs text-muted-foreground">The grades are in the Quality review above the article.</p>
      </div>
    ) : (
      <div data-testid="checks-qa" data-qa="none">
        <p className="text-sm font-medium text-foreground">Not scored</p>
        <p className="mt-0.5 text-xs text-muted-foreground">No quality check was recorded for this item, so read it carefully before you approve it.</p>
      </div>
    );
  }
  const verdict = qaVerdict(qa);
  const known = new Set(qa.checks.map((c) => c.name.toLowerCase()));
  // Failures the worker named without a check row. Gate failures are already listed under the gates.
  const extraFailing = qa.failing.filter((f) => !known.has(f.toLowerCase()) && !/ gate$/i.test(f));
  const rows = [
    ...qa.checks.map((c) => ({ name: c.name, ok: c.ok, detail: c.detail })),
    ...extraFailing.map((f) => ({ name: f, ok: false, detail: undefined as string | undefined })),
  ];
  const shown = all ? rows : rows.slice(0, CHECKS_SHOWN);
  const tone = verdict.tone === "bad" ? "text-red-300" : verdict.tone === "good" ? "text-emerald-300" : "text-foreground";

  return (
    <div className="space-y-3" data-testid="checks-qa" data-qa={verdict.tone}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {qa.score !== null && (
          <p className="text-3xl font-bold leading-none text-foreground" data-testid="qa-score">
            {Math.round(qa.score)}
            <span className="text-base font-medium text-muted-foreground">/100</span>
          </p>
        )}
        <p className={`text-sm font-semibold ${tone}`} data-testid="qa-verdict">{verdict.label}</p>
      </div>
      {qa.checkedAt && fmtDateTime(qa.checkedAt) && <p className="-mt-1 text-xs text-muted-foreground">Checked {fmtDateTime(qa.checkedAt)}</p>}

      {qa.gates.length > 0 && (
        <ul className="space-y-1" data-testid="qa-gates">
          {qa.gates.map((g) => {
            const bad = g.passed === false;
            return (
              <li key={g.label} className="flex items-center gap-2 text-sm">
                {bad ? <XCircle size={15} className="shrink-0 text-red-400" /> : <CheckCircle2 size={15} className="shrink-0 text-emerald-400" />}
                <span className="min-w-0 flex-1 break-words text-foreground">{g.label}</span>
                <span className={`shrink-0 text-xs ${bad ? "text-red-300" : "text-muted-foreground"}`}>
                  {g.score !== null ? `${Math.round(g.score)}/100` : g.passed === true ? "passed" : bad ? "failed" : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground" data-testid="qa-counts">
            {qa.checksPassed ?? 0} passed, {qa.checksFailed ?? rows.filter((r) => !r.ok).length} failed
          </p>
          <ul className="space-y-1.5" data-testid="qa-checks">
            {shown.map((r, i) => (
              <li key={`${r.name}-${i}`} className="flex items-start gap-2 text-sm" data-ok={r.ok}>
                {r.ok ? (
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-400" aria-label="Passed" />
                ) : (
                  <XCircle size={15} className="mt-0.5 shrink-0 text-red-400" aria-label="Failed" />
                )}
                <span className="min-w-0 break-words">
                  <span className={r.ok ? "text-foreground/90" : "text-red-200"}>{humanizeCheckName(r.name)}</span>
                  {r.detail && <span className="block text-xs text-muted-foreground">{r.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
          {rows.length > CHECKS_SHOWN && (
            <button type="button" className="text-xs text-blue-400 underline underline-offset-2" onClick={() => setAll((v) => !v)} data-testid="button-qa-show-all">
              {all ? "Show fewer checks" : `Show all ${rows.length} checks`}
            </button>
          )}
        </div>
      )}
      {rows.length === 0 && qa.gates.length === 0 && qa.score === null && (
        <p className="text-xs text-muted-foreground">A quality record exists, but it has no scores or checks to show.</p>
      )}
    </div>
  );
}

export interface ChecksPanelProps {
  item: StudioItem;
  workspace: any;
  /** Sources counted from the article ledger, else from the item own metadata. */
  sourcesCount: number | null;
  /** True when the Sources panel below the article exists, so "See list" has somewhere to go. */
  hasSourcesPanel: boolean;
  /** How many ledger facts rest on law that is not in force yet (proposed, in Parliament, or passed but not yet in effect). */
  notInForce?: number;
  /** A score card or an independent QA review is shown above the article. */
  graded?: boolean;
  onSeeSources?: () => void;
  /** Collapsed details block for small screens; an always-open card otherwise. */
  collapsible?: boolean;
}

/** Everything the owner needs to trust (or distrust) a draft, next to the draft itself. */
export function ChecksPanel({ item, workspace, sourcesCount, hasSourcesPanel, notInForce = 0, graded = false, onSeeSources, collapsible = false }: ChecksPanelProps) {
  const kind = kindOf(item);
  const qa = parseQa(item);
  const verdict = qaVerdict(qa);
  const words = wordCountOf(item);
  const unverified = unverifiedOf(item);
  const dest = destinationOf(item, workspace, {
    wordpress: wordpressConfigured(workspace),
    platform: itemPlatformConnected(workspace, item),
  });
  const origin = originOf(item);

  const sourcesText = sourcesCount !== null && sourcesCount > 0 ? `${sourcesCount} cited` : kind === "article" ? "None listed" : "—";

  const body = (
    <div className="space-y-4" data-testid="checks-body">
      <QaBlock qa={qa} graded={graded} />
      <GateBlock item={item} />
      <VerifyBlock item={item} />

      <dl className="divide-y divide-border border-t border-border pt-1" data-testid="checks-facts">
        {(kind === "article" || (sourcesCount ?? 0) > 0) && (
          <Row label="Sources" testId="fact-sources">
            {sourcesText}
            {hasSourcesPanel && onSeeSources && (
              <button type="button" onClick={onSeeSources} className="ml-2 text-xs text-blue-400 underline underline-offset-2" data-testid="button-see-sources">
                See list
              </button>
            )}
            {notInForce > 0 && (
              <span className="block text-xs text-amber-300" data-testid="sources-not-in-force">
                {notInForce} {notInForce === 1 ? "rests" : "rest"} on law that is not in force yet
              </span>
            )}
          </Row>
        )}
        <Row label="Length" testId="fact-words">{words > 0 ? `${words.toLocaleString()} words` : "—"}</Row>
        <Row label="Written by" testId="fact-agent">
          {agentFor(item).name}
          <span className="block text-xs text-muted-foreground">{origin.label}</span>
        </Row>
        <Row label="Created" testId="fact-created">{fmtDate(item.createdAt) || "—"}</Row>
        <Row label="Goes to" testId="fact-destination">
          <span className={dest.ok === false ? "text-amber-300" : undefined}>{dest.text}</span>
        </Row>
      </dl>

      <div className="space-y-1.5 border-t border-border pt-3" data-testid="checks-unverified">
        <div className="flex items-center gap-2">
          <CircleHelp size={14} className="shrink-0 text-amber-300" />
          <h3 className="text-sm font-semibold text-foreground">Could not verify</h3>
          {unverified.length > 0 && <span className="rounded-full bg-amber-500/20 px-1.5 text-[11px] font-bold text-amber-300">{unverified.length}</span>}
        </div>
        {unverified.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="unverified-none">Nothing was flagged as unverified.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90" data-testid="unverified-list">
            {unverified.map((u, i) => (
              <li key={i} className="break-words">{u}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const verdictChip = (
    <Chip className={qa ? qaTone(qa) : "border-zinc-500/30 bg-zinc-500/15 text-zinc-300"}>
      {qa && qa.score !== null ? `${Math.round(qa.score)}/100 · ` : ""}
      {verdict.label}
    </Chip>
  );

  if (collapsible) {
    return (
      <details className="group rounded-2xl border border-border bg-card/70" data-testid="checks-panel" data-layout="collapsed">
        <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
          <ChevronRight size={16} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
          <ListChecks size={15} className="shrink-0 text-muted-foreground" />
          Checks
          {verdictChip}
          <span className="text-xs font-normal text-muted-foreground">
            {words > 0 ? `${words.toLocaleString()} words` : ""}
            {sourcesCount ? ` · ${sourcesCount} sources` : ""}
            {unverified.length ? ` · ${unverified.length} unverified` : ""}
          </span>
        </summary>
        <div className="border-t border-border px-4 py-4">{body}</div>
      </details>
    );
  }

  return (
    <section className="rounded-2xl border border-border bg-card/70 p-4" data-testid="checks-panel" data-layout="side">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ListChecks size={16} className="text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Checks</h2>
        <span className="ml-auto">{verdictChip}</span>
      </div>
      {body}
    </section>
  );
}
